import { useCallback, useEffect, useMemo, useState } from 'react'
import { Building2, Loader2, Phone, Search, TrendingDown, TrendingUp } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import { fetchHqDashboard, lookupHqCustomerByPhone, pushHqPackagePrices, pushHqServicePrices } from '@/lib/api/hq'
import { fetchMasterPackages } from '@/lib/api/masterPackages'
import { fetchServices } from '@/lib/api/services'
import { formatReportMoney, resolveReportPeriodBounds } from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { hasPermission, PERMISSIONS } from '@/lib/permissions'
import { cn } from '@/lib/utils'

export function HqDashboardPage() {
  const { user } = useAuth()
  const { activeBranchId } = useBranch()
  const canManage = hasPermission(user, PERMISSIONS.HQ_MANAGE) || hasPermission(user, PERMISSIONS.ADMIN_FULL)

  const [period, setPeriod] = useState('month')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [phoneQuery, setPhoneQuery] = useState('')
  const [lookupResult, setLookupResult] = useState(null)
  const [lookupLoading, setLookupLoading] = useState(false)

  const [services, setServices] = useState([])
  const [packages, setPackages] = useState([])
  const [pushForm, setPushForm] = useState({
    serviceId: '',
    packageId: '',
    priceLadies: '',
    priceGents: '',
    salePrice: '',
    targetBranchIds: [],
  })
  const [pushMessage, setPushMessage] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const loadDashboard = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setDashboard(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchHqDashboard({
        period,
        from: period === 'custom' ? customFrom : bounds?.from,
        to: period === 'custom' ? customTo : bounds?.to,
      })
      setDashboard(data)
      setPushForm((current) => ({
        ...current,
        targetBranchIds: (data.branches ?? []).map((row) => row.branch_id),
      }))
    } catch (err) {
      setDashboard(null)
      setError(apiErrorMessage(err, 'Unable to load HQ dashboard.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, bounds])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard, activeBranchId])

  useEffect(() => {
    if (!canManage) return

    Promise.all([fetchServices(), fetchMasterPackages()])
      .then(([serviceRows, packageRows]) => {
        setServices(serviceRows ?? [])
        setPackages(packageRows ?? [])
      })
      .catch(() => {
        setServices([])
        setPackages([])
      })
  }, [canManage, activeBranchId])

  const handleLookup = async (event) => {
    event.preventDefault()
    setLookupLoading(true)
    setError('')
    try {
      const result = await lookupHqCustomerByPhone(phoneQuery)
      setLookupResult(result)
    } catch (err) {
      setLookupResult(null)
      setError(apiErrorMessage(err, 'Unable to search guests.'))
    } finally {
      setLookupLoading(false)
    }
  }

  const toggleBranch = (branchId) => {
    setPushForm((current) => {
      const exists = current.targetBranchIds.includes(branchId)
      return {
        ...current,
        targetBranchIds: exists
          ? current.targetBranchIds.filter((id) => id !== branchId)
          : [...current.targetBranchIds, branchId],
      }
    })
  }

  const handleServicePush = async (event) => {
    event.preventDefault()
    setPushMessage('')
    setError('')
    try {
      const result = await pushHqServicePrices({
        service_id: Number(pushForm.serviceId),
        branch_ids: pushForm.targetBranchIds,
        price_ladies: pushForm.priceLadies ? Number(pushForm.priceLadies) : undefined,
        price_gents: pushForm.priceGents ? Number(pushForm.priceGents) : undefined,
      })
      setPushMessage(`Updated ${result.updated?.length ?? 0} branch(es).`)
      await loadDashboard()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to push service prices.'))
    }
  }

  const handlePackagePush = async (event) => {
    event.preventDefault()
    setPushMessage('')
    setError('')
    try {
      const result = await pushHqPackagePrices({
        package_id: Number(pushForm.packageId),
        branch_ids: pushForm.targetBranchIds,
        sale_price: pushForm.salePrice ? Number(pushForm.salePrice) : undefined,
      })
      setPushMessage(`Updated ${result.updated?.length ?? 0} package row(s).`)
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to push package prices.'))
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 lg:px-8">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Building2 className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">HQ dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Consolidated KPIs, branch ranking, guest lookup, and central pricing.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <ReportFilterBar
          period={period}
          compare="none"
          onPeriodChange={setPeriod}
          onCompareChange={() => {}}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />

        {error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading HQ dashboard…
          </div>
        ) : null}

        {!loading && dashboard ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <SummaryCard
                label={`Total HQ gross (${dashboard.hq_currency})`}
                value={dashboard.summary?.total_hq_gross ?? '—'}
              />
              <SummaryCard label="Branches" value={String(dashboard.summary?.branch_count ?? 0)} />
              <SummaryCard label="Appointments" value={String(dashboard.summary?.total_appointments ?? 0)} />
              <SummaryCard
                label={`HQ commission (${dashboard.hq_currency})`}
                value={dashboard.summary?.total_hq_commission ?? '—'}
              />
            </div>

            {dashboard.multi_currency_footnote ? (
              <p className="text-xs text-muted-foreground">{dashboard.multi_currency_footnote}</p>
            ) : null}

            <section className="card-surface overflow-hidden">
              <div className="border-b border-border/60 px-4 py-3">
                <h2 className="text-sm font-semibold">Branch performance ranking</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2">#</th>
                      <th className="px-4 py-2">Branch</th>
                      <th className="px-4 py-2">HQ gross</th>
                      <th className="px-4 py-2">Sales</th>
                      <th className="px-4 py-2">Appts</th>
                      <th className="px-4 py-2">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dashboard.ranking ?? []).map((row) => (
                      <tr key={row.branch_id} className="border-t border-border/50">
                        <td className="px-4 py-3 font-semibold tabular-nums">{row.rank}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{row.branch_name}</p>
                          <p className="text-xs text-muted-foreground">{row.currency_code}</p>
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {row.hq_gross_total
                            ? `${dashboard.hq_currency} ${formatReportMoney(row.hq_gross_total)}`
                            : '—'}
                        </td>
                        <td className="px-4 py-3 tabular-nums">{row.sale_count}</td>
                        <td className="px-4 py-3 tabular-nums">{row.appointment_count}</td>
                        <td className="px-4 py-3">
                          <TrendBadge value={row.trend_pct} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : null}

        <section className="card-surface p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Phone className="h-4 w-4" aria-hidden />
            Cross-branch guest lookup
          </h2>
          <form onSubmit={handleLookup} className="mt-3 flex flex-wrap gap-2">
            <input
              className="min-w-[220px] flex-1 rounded-lg border border-input px-3 py-2 text-sm"
              placeholder="Phone number"
              value={phoneQuery}
              onChange={(e) => setPhoneQuery(e.target.value)}
              required
            />
            <Button type="submit" disabled={lookupLoading}>
              {lookupLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              Search
            </Button>
          </form>
          {(lookupResult?.matches ?? []).length > 0 ? (
            <ul className="mt-3 space-y-2">
              {lookupResult.matches.map((match) => (
                <li key={`${match.branch_id}-${match.id}`} className="rounded-lg border border-border/60 px-3 py-2 text-sm">
                  <p className="font-medium">{match.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {match.branch_name} · {match.phone}
                    {match.preferred_stylist ? ` · ${match.preferred_stylist}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          ) : lookupResult ? (
            <p className="mt-3 text-sm text-muted-foreground">No matching guests.</p>
          ) : null}
        </section>

        {canManage ? (
          <section className="card-surface space-y-4 p-4">
            <h2 className="text-sm font-semibold">Central pricing push</h2>
            <p className="text-xs text-muted-foreground">
              Matches services and packages by name at each selected branch.
            </p>

            <div className="flex flex-wrap gap-2">
              {(dashboard?.branches ?? []).map((branch) => (
                <label
                  key={branch.branch_id}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs',
                    pushForm.targetBranchIds.includes(branch.branch_id)
                      ? 'border-primary bg-primary/10'
                      : 'border-border/60',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={pushForm.targetBranchIds.includes(branch.branch_id)}
                    onChange={() => toggleBranch(branch.branch_id)}
                  />
                  {branch.branch_name}
                </label>
              ))}
            </div>

            <form onSubmit={handleServicePush} className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <label className="block text-xs md:col-span-2">
                Source service (current branch)
                <select
                  className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm"
                  value={pushForm.serviceId}
                  onChange={(e) => setPushForm((c) => ({ ...c, serviceId: e.target.value }))}
                  required
                >
                  <option value="">Select service</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                Ladies price
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm"
                  value={pushForm.priceLadies}
                  onChange={(e) => setPushForm((c) => ({ ...c, priceLadies: e.target.value }))}
                />
              </label>
              <label className="block text-xs">
                Gents price
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm"
                  value={pushForm.priceGents}
                  onChange={(e) => setPushForm((c) => ({ ...c, priceGents: e.target.value }))}
                />
              </label>
              <div className="md:col-span-4">
                <Button type="submit">Push service prices</Button>
              </div>
            </form>

            <form onSubmit={handlePackagePush} className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <label className="block text-xs md:col-span-2">
                Source package (current branch)
                <select
                  className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm"
                  value={pushForm.packageId}
                  onChange={(e) => setPushForm((c) => ({ ...c, packageId: e.target.value }))}
                  required
                >
                  <option value="">Select package</option>
                  {packages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs md:col-span-2">
                Sale price
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm"
                  value={pushForm.salePrice}
                  onChange={(e) => setPushForm((c) => ({ ...c, salePrice: e.target.value }))}
                />
              </label>
              <div className="md:col-span-4">
                <Button type="submit" variant="outline">
                  Push package prices
                </Button>
              </div>
            </form>

            {pushMessage ? <p className="text-sm text-emerald-700">{pushMessage}</p> : null}
          </section>
        ) : null}
      </div>
    </div>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card px-4 py-3 shadow-luxury-sm">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  )
}

/**
 * @param {{ value: number | null | undefined }} props
 */
function TrendBadge({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-xs text-muted-foreground">—</span>
  }

  const positive = value >= 0
  const Icon = positive ? TrendingUp : TrendingDown

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        positive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700',
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {positive ? '+' : ''}
      {value}%
    </span>
  )
}
